import { useCallback } from 'react';
import { GoogleMap, LoadScriptNext } from '@react-google-maps/api';
import debounce from 'lodash/debounce';

import { Coordinates, IStore, GoogleMapsOptions } from '@ocx-app/modules/stores/types';

import { StoreMarkers } from './components/store-markers/store-markers.component';
import { CurrentLocation } from './components/current-location/current-location.component';
import { MapControlContainer } from './components/map-control-container/map-control-container.component';
import { Loading } from './components/loading.component';
import { MAP_ZOOM_CHANGE_TIMEOUT } from './store-locator.constants';
import classes from './store-locator.module.css';

export type StoreLocatorProps = {
  loading: boolean;
  googleMapsApiKey?: string;
  options: GoogleMapsOptions;
  stores: IStore[];
  selectedStore: IStore | null;
  currentLocation: Coordinates;
  targetLocation: Coordinates;
  isNoStoresFound: boolean;
  isLocationGranted: boolean;

  // eslint-disable-next-line no-undef
  onMapLoaded(map: google.maps.Map): void;
  onStorePinClick(store: IStore | null): void;
  onNavigateToListViewClick(): void;
  onStoreClick(storeId: string): void;
  onCurrentLocationClick(): void;
  onZoomChanged(): void;
  onDragStart(): void;
  onDragEnd(): void;
  disabledControls: boolean;
};

export const StoreLocator = (props: StoreLocatorProps) => {
  const {
    googleMapsApiKey = '',
    loading,
    stores,
    selectedStore,
    currentLocation,
    targetLocation,
    isNoStoresFound,
    isLocationGranted,
    options,

    onStorePinClick,
    onNavigateToListViewClick,
    onStoreClick,
    onCurrentLocationClick,
    onMapLoaded,
    onZoomChanged,
    onDragStart,
    onDragEnd,
    disabledControls,
  } = props;

  const onClickMap = useCallback(() => onStorePinClick(null), [onStorePinClick]);

  return (
    <LoadScriptNext googleMapsApiKey={googleMapsApiKey} loadingElement={<div /> /* TODO add loader */}>
      <>
        <GoogleMap
          onClick={onClickMap}
          mapContainerClassName={classes.mapContainer}
          center={targetLocation}
          options={options}
          onDragEnd={onDragEnd}
          onZoomChanged={debounce(onZoomChanged, MAP_ZOOM_CHANGE_TIMEOUT)}
          onDragStart={onDragStart}
          onLoad={onMapLoaded}>
          {isLocationGranted && <CurrentLocation currentLocation={currentLocation} />}
          <StoreMarkers stores={stores} onStorePinClick={onStorePinClick} />
        </GoogleMap>
        <Loading open={loading} />
        <MapControlContainer
          selectedStore={selectedStore}
          isNoStoresFound={isNoStoresFound}
          onNavigateToListViewClick={onNavigateToListViewClick}
          onStoreClick={onStoreClick}
          onCurrentLocationClick={onCurrentLocationClick}
          isLocationGranted={isLocationGranted}
          disabledControls={disabledControls}
        />
      </>
    </LoadScriptNext>
  );
};
